import {
  type Calendar,
  createCalendar,
  DayGrid,
  destroyCalendar,
  Interaction,
  ResourceTimeGrid,
  TimeGrid,
} from "@event-calendar/core";
import { useEffect, useRef } from "react";

export type EventCalendarOptions = Calendar.Options;

const PLUGINS = [ResourceTimeGrid, TimeGrid, DayGrid, Interaction];

export interface EventCalendarAdapterProps {
  readonly options: EventCalendarOptions;
  readonly className?: string;
  readonly "data-testid"?: string;
}

const DATA_KEYS = new Set<keyof Calendar.Options>([
  "view",
  "date",
  "timeZone",
  "events",
  "resources",
  "editable",
  "selectable",
  "height",
  "slotMinTime",
  "slotMaxTime",
  "slotDuration",
  // Высота слота в px: сетка подгоняется под коробку, а не наоборот (§3 frontend-architecture).
  "slotHeight",
  // true включает ec-uniform: строки месяца делят коробку поровну, лишнее — в «+N ещё».
  "dayMaxEvents",
  "filterEventsWithResources",
  "headerToolbar",
  "nowIndicator",
  "firstDay",
]);

const INVALID_CALENDAR_STRUCTURE_ROLES = new Set([
  "table",
  "rowgroup",
  "row",
  "columnheader",
  "cell",
  "gridcell",
]);
const INVALID_CALENDAR_STRUCTURE_ATTRIBUTES = [
  "aria-colcount",
  "aria-colindex",
  "aria-colspan",
  "aria-rowcount",
  "aria-rowindex",
  "aria-rowspan",
] as const;

function removeInvalidStructuralRoles(host: HTMLElement): void {
  for (const node of host.querySelectorAll<HTMLElement>("[role]")) {
    const role = node.getAttribute("role");
    if (role && INVALID_CALENDAR_STRUCTURE_ROLES.has(role)) {
      node.removeAttribute("role");
    }
  }
  const selector = INVALID_CALENDAR_STRUCTURE_ATTRIBUTES.map((name) => `[${name}]`).join(",");
  for (const node of host.querySelectorAll<HTMLElement>(selector)) {
    for (const attribute of INVALID_CALENDAR_STRUCTURE_ATTRIBUTES) {
      node.removeAttribute(attribute);
    }
  }
  for (const scrollRegion of host.querySelectorAll<HTMLElement>(".ec-main")) {
    scrollRegion.tabIndex = 0;
    scrollRegion.setAttribute("aria-label", "Прокручиваемая сетка календаря");
  }
  // Стрелки периода библиотека рисует иконкой без текста — имя даём сами.
  for (const [selector, label] of [
    [".ec-button.ec-prev", "Предыдущий период"],
    [".ec-button.ec-next", "Следующий период"],
  ] as const) {
    for (const button of host.querySelectorAll<HTMLElement>(selector)) {
      if (!button.getAttribute("aria-label")) {
        button.setAttribute("aria-label", label);
      }
    }
  }
}

/**
 * Local React lifecycle adapter for EventCalendar core (MIT).
 * Mounts createCalendar / destroyCalendar; data options via setOption.
 * Callbacks are held in a ref so identity churn does not thrash the calendar DOM.
 */
export function EventCalendarAdapter(props: EventCalendarAdapterProps) {
  const hostRef = useRef<HTMLElement | null>(null);
  const calendarRef = useRef<Calendar | null>(null);
  const optionsRef = useRef(props.options);
  optionsRef.current = props.options;
  const lastDataJson = useRef<string>("");

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const bridge: Calendar.Options = {
      ...optionsRef.current,
      datesSet: (info) => optionsRef.current.datesSet?.(info),
      eventClick: (info) => optionsRef.current.eventClick?.(info),
      dateClick: (info) => optionsRef.current.dateClick?.(info),
      select: (info) => optionsRef.current.select?.(info),
      eventDrop: (info) => optionsRef.current.eventDrop?.(info),
      eventResize: (info) => optionsRef.current.eventResize?.(info),
      eventContent: (arg) => {
        const content = optionsRef.current.eventContent;
        return typeof content === "function" ? content(arg) : content;
      },
    };

    const calendar = createCalendar(host, PLUGINS, bridge);
    calendarRef.current = calendar;
    lastDataJson.current = serializeData(optionsRef.current);
    removeInvalidStructuralRoles(host);
    const ariaObserver = new MutationObserver(() => removeInvalidStructuralRoles(host));
    ariaObserver.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", ...INVALID_CALENDAR_STRUCTURE_ATTRIBUTES],
    });

    return () => {
      ariaObserver.disconnect();
      calendarRef.current = null;
      void destroyCalendar(calendar);
    };
  }, []);

  useEffect(() => {
    const calendar = calendarRef.current;
    if (!calendar) {
      return;
    }
    const serialized = serializeData(props.options);
    if (serialized === lastDataJson.current) {
      return;
    }
    lastDataJson.current = serialized;
    for (const key of DATA_KEYS) {
      if (key in props.options) {
        calendar.setOption(key, props.options[key]);
      }
    }
  }, [props.options]);

  return (
    <section
      ref={hostRef}
      className={props.className}
      data-testid={props["data-testid"] ?? "event-calendar"}
      aria-label="Календарь занятий"
    />
  );
}

function serializeData(options: Calendar.Options): string {
  const events = (options.events ?? []).map((e) => {
    const start = "start" in e ? e.start : null;
    const end = "end" in e ? e.end : null;
    return {
      id: "id" in e ? e.id : null,
      start: start instanceof Date ? start.toISOString() : start,
      end: end instanceof Date ? end.toISOString() : end,
      title: "title" in e ? e.title : null,
      resourceIds: "resourceIds" in e ? e.resourceIds : "resourceId" in e ? e.resourceId : null,
      classNames: "classNames" in e ? e.classNames : null,
      extendedProps: "extendedProps" in e ? e.extendedProps : null,
    };
  });
  const resources = (options.resources ?? []).map((r) => ({ id: r.id, title: r.title }));
  return JSON.stringify({
    view: options.view,
    date: options.date instanceof Date ? options.date.toISOString() : options.date,
    events,
    resources,
    editable: options.editable,
    selectable: options.selectable,
    height: options.height,
    slotMinTime: options.slotMinTime,
    slotMaxTime: options.slotMaxTime,
    slotDuration: options.slotDuration,
    slotHeight: options.slotHeight,
    dayMaxEvents: options.dayMaxEvents,
    filterEventsWithResources: options.filterEventsWithResources,
    headerToolbar: options.headerToolbar,
    nowIndicator: options.nowIndicator,
    timeZone: options.timeZone,
    firstDay: options.firstDay,
  });
}

export { createCalendar, DayGrid, destroyCalendar, ResourceTimeGrid, TimeGrid, Interaction };
